import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import Icon from '../../../components/AppIcon';
import './LocationInput.css';

// Importation des données locales
import countriesData from '../../../country/countriesData.json';

// Composant Input (supposé déjà converti)
import Input from '../../../components/ui/Input';

// Configuration de react-modal
Modal.setAppElement('#root');

// Fuseaux horaires approximatifs par pays
const countryTimezones = {
  'CM': 'Africa/Douala',
  'FR': 'Europe/Paris',
  'US': 'America/New_York',
  'GB': 'Europe/London',
  'DE': 'Europe/Berlin',
  'ES': 'Europe/Madrid',
  'IT': 'Europe/Rome',
  'CN': 'Asia/Shanghai',
  'JP': 'Asia/Tokyo',
  'IN': 'Asia/Kolkata',
  'BR': 'America/Sao_Paulo',
  'CA': 'America/Toronto',
  'AU': 'Australia/Sydney',
  'MX': 'America/Mexico_City',
  'RU': 'Europe/Moscow',
  'ZA': 'Africa/Johannesburg'
};

// Styles pour react-modal
const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  content: {
    position: 'relative',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    padding: '0',
    borderRadius: '16px',
    border: 'none',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
  }
};

// Liste des APIs de géocodage avec leurs priorités
const GEOCODING_APIS = [
  {
    name: 'BigDataCloud',
    url: (lat, lon) => `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=fr`,
    parser: (data) => {
      if (!data) return null;
      
      let countryCode = data.countryCode || '';
      let city = data.city || data.locality || '';
      
      // Si la ville est vide mais on a une région, l'utiliser
      if (!city && data.principalSubdivision) {
        city = data.principalSubdivision;
      }
      
      return {
        city: city,
        countryCode: countryCode,
        countryName: data.countryName || '',
        region: data.principalSubdivision || '',
        locality: data.locality || '',
        continent: data.continent || '',
        latitude: data.latitude,
        longitude: data.longitude,
        source: 'BigDataCloud'
      };
    }
  },
  {
    name: 'Nominatim',
    url: (lat, lon) => `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1&accept-language=fr&zoom=10`,
    parser: (data) => {
      if (!data || !data.address) return null;
      
      let countryCode = data.address.country_code ? data.address.country_code.toUpperCase() : '';
      let city = data.address.city || 
                data.address.town || 
                data.address.village || 
                data.address.municipality ||
                data.address.county ||
                '';
      
      return {
        city: city,
        countryCode: countryCode,
        countryName: data.address.country || '',
        region: data.address.state || data.address.region || '',
        locality: data.display_name || '',
        latitude: parseFloat(data.lat),
        longitude: parseFloat(data.lon),
        source: 'Nominatim'
      };
    }
  },
  {
    name: 'Geoapify',
    url: (lat, lon) => `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lon}&apiKey=c40e8d5d8e7f4b4e8a5d5e5a5d5e5a5e&format=json`,
    parser: (data) => {
      if (!data || !data.results || data.results.length === 0) return null;
      
      const result = data.results[0];
      return {
        city: result.city || result.county || '',
        countryCode: result.country_code ? result.country_code.toUpperCase() : '',
        countryName: result.country || '',
        region: result.state || '',
        locality: result.formatted || '',
        latitude: result.lat,
        longitude: result.lon,
        source: 'Geoapify'
      };
    }
  }
];

const LocationInput = ({ location, onLocationChange }) => {
  const { t } = useTranslation();
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState('');
  const [recentLocations, setRecentLocations] = useState([]);
  const [geocodingAttempts, setGeocodingAttempts] = useState([]);
  
  // États pour les modales
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  // Charger les localisations récentes et sauvegardées
  useEffect(() => {
    loadRecentLocations();
    loadSavedLocation();
  }, []);

  const loadRecentLocations = useCallback(async () => {
    try {
      const recent = localStorage.getItem('recentLocations');
      if (recent) {
        setRecentLocations(JSON.parse(recent));
      }
    } catch (error) {
      console.error(t('locationInput.errors.loadRecent'), error);
    }
  }, [t]);

  const loadSavedLocation = useCallback(async () => {
    try {
      const savedLocation = localStorage.getItem('userLocation');
      if (savedLocation && !location?.city) {
        const parsed = JSON.parse(savedLocation);
        onLocationChange({
          ...location,
          ...parsed
        });
      }
    } catch (error) {
      console.error(t('locationInput.errors.loadSaved'), error);
    }
  }, [location, onLocationChange, t]);

  const saveToRecent = useCallback(async (locationData) => {
    try {
      const recent = [...recentLocations];
      const existingIndex = recent.findIndex(loc =>
        loc.city === locationData.city &&
        loc.countryCode === locationData.countryCode
      );

      if (existingIndex > -1) {
        recent.splice(existingIndex, 1);
      }

      const country = countriesData.find(c => c.code === locationData.countryCode);
      
      recent.unshift({
        city: locationData.city,
        countryCode: locationData.countryCode,
        countryName: country?.name || locationData.countryName,
        countryFlag: country?.flag || '🌍',
        timestamp: Date.now()
      });

      const limitedRecent = recent.slice(0, 6);
      setRecentLocations(limitedRecent);
      localStorage.setItem('recentLocations', JSON.stringify(limitedRecent));
    } catch (error) {
      console.error(t('locationInput.errors.saveRecent'), error);
    }
  }, [recentLocations, t]);

  const requestLocationPermission = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.permissions || !navigator.permissions.query) {
        // Fallback pour les navigateurs qui ne supportent pas l'API permissions
        resolve('prompt');
        return;
      }

      navigator.permissions.query({ name: 'geolocation' })
        .then((result) => {
          resolve(result.state);
        })
        .catch(() => {
          resolve('prompt');
        });
    });
  }, []);

  const geocodeWithAPI = useCallback(async (lat, lon, apiConfig, timeout = 8000) => {
    return new Promise(async (resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Timeout for ${apiConfig.name}`));
      }, timeout);

      try {
        console.log(`Trying ${apiConfig.name} API...`);
        const response = await fetch(apiConfig.url(lat, lon), {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'WeatherApp/1.0 (contact@weatherapp.com)'
          }
        });

        if (!response.ok) {
          throw new Error(`${apiConfig.name} API error: ${response.status}`);
        }

        const data = await response.json();
        const parsedData = apiConfig.parser(data);
        
        if (parsedData && (parsedData.city || parsedData.countryCode)) {
          clearTimeout(timeoutId);
          console.log(`Success with ${apiConfig.name}:`, parsedData);
          resolve({ ...parsedData, apiUsed: apiConfig.name });
        } else {
          throw new Error(`${apiConfig.name} returned incomplete data`);
        }
      } catch (apiError) {
        clearTimeout(timeoutId);
        console.warn(`Failed with ${apiConfig.name}:`, apiError.message);
        reject(apiError);
      }
    });
  }, []);

  const geocodeCoordinates = useCallback(async (latitude, longitude) => {
    const attempts = [];
    
    // Essayer chaque API dans l'ordre
    for (const apiConfig of GEOCODING_APIS) {
      try {
        const result = await geocodeWithAPI(latitude, longitude, apiConfig);
        attempts.push({ api: apiConfig.name, success: true, data: result });
        
        // Si on a un résultat valide, le retourner
        if (result.city || result.countryCode) {
          setGeocodingAttempts(attempts);
          return result;
        }
      } catch (error) {
        attempts.push({ api: apiConfig.name, success: false, error: error.message });
        continue; // Essayer l'API suivante
      }
    }
    
    setGeocodingAttempts(attempts);
    throw new Error('All geocoding APIs failed');
  }, [geocodeWithAPI]);

  const detectCurrentLocation = useCallback(async () => {
    setIsDetecting(true);
    setError('');
    setGeocodingAttempts([]);

    try {
      const permissionState = await requestLocationPermission();
      
      if (permissionState === 'denied') {
        setError(t('locationInput.errors.permissionDenied'));
        setIsDetecting(false);
        return;
      }

      if (!navigator.geolocation) {
        setError(t('locationInput.errors.geolocationNotSupported'));
        setIsDetecting(false);
        return;
      }

      // Fermer toutes les modales avant de démarrer la géolocalisation
      setShowCountryModal(false);
      setShowCityModal(false);

      const geolocationPromise = new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 600000
        });
      });

      // Timeout pour éviter que la requête ne bloque trop longtemps
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('timeout')), 20000);
      });

      const position = await Promise.race([geolocationPromise, timeoutPromise]);
      
      const { latitude, longitude } = position.coords;
      console.log('Coordinates obtained:', latitude, longitude);
     
      try {
        // Essayer de géocoder avec les différentes APIs
        const geocodedData = await geocodeCoordinates(latitude, longitude);
        
        // Trouver les informations du pays dans nos données
        let country = null;
        if (geocodedData.countryCode) {
          country = countriesData.find(c => c.code === geocodedData.countryCode);
        }
        
        // Déterminer la ville
        let city = geocodedData.city;
        if (!city && geocodedData.locality) {
          // Extraire la première partie de la localité
          city = geocodedData.locality.split(',')[0].trim();
        }
        
        // Si toujours pas de ville, utiliser la région ou la capitale du pays
        if (!city && geocodedData.region) {
          city = geocodedData.region;
        } else if (!city && country) {
          city = country.capital;
        }

        const locationData = {
          city: city || '',
          countryCode: geocodedData.countryCode || '',
          countryName: country?.name || geocodedData.countryName || '',
          countryFlag: country?.flag || '🌍',
          coordinates: { lat: latitude, lng: longitude },
          address: geocodedData.locality || geocodedData.countryName || '',
          isGPSDetected: true,
          source: geocodedData.apiUsed
        };

        console.log('Final location data:', locationData);

        onLocationChange({
          ...location,
          ...locationData
        });

        localStorage.setItem('userLocation', JSON.stringify(locationData));
        await saveToRecent(locationData);
        
        setIsDetecting(false);
      } catch (apiError) {
        console.error('All geocoding APIs failed:', apiError);
        console.log('API attempts:', geocodingAttempts);
        
        // Fallback basé sur le fuseau horaire
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const fallbackLocation = getLocationByTimezone(timezone, latitude, longitude);
        
        console.log('Using fallback location:', fallbackLocation);
        
        onLocationChange({
          ...location,
          ...fallbackLocation,
          isFallback: true
        });
        
        // Sauvegarder le fallback
        localStorage.setItem('userLocation', JSON.stringify(fallbackLocation));
        await saveToRecent(fallbackLocation);
        
        setError(t('locationInput.errors.usingFallback'));
        setIsDetecting(false);
      }
    } catch (error) {
      console.error('Geolocation error:', error);
      
      if (error.code === 1 || error.message === 'User denied Geolocation') {
        setError(t('locationInput.errors.permissionDenied'));
      } else if (error.message === 'timeout') {
        setError(t('locationInput.errors.timeout'));
      } else {
        setError(t('locationInput.errors.cannotDetermine'));
      }
      setIsDetecting(false);
    }
  }, [location, onLocationChange, requestLocationPermission, saveToRecent, geocodeCoordinates, geocodingAttempts, t]);

  const getLocationByTimezone = useCallback((timezone, lat, lng) => {
    console.log('Detected timezone:', timezone);
    
    if (!timezone) {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    
    // Chercher une correspondance exacte d'abord
    for (const [code, tz] of Object.entries(countryTimezones)) {
      if (timezone === tz) {
        const country = countriesData.find(c => c.code === code);
        if (country) {
          console.log('Exact timezone match:', country.name);
          return createFallbackLocation(country, lat, lng);
        }
      }
    }
    
    // Chercher une correspondance partielle
    const tzParts = timezone.split('/');
    if (tzParts.length > 1) {
      const region = tzParts[1];
      for (const [code, tz] of Object.entries(countryTimezones)) {
        if (tz.includes(region)) {
          const country = countriesData.find(c => c.code === code);
          if (country) {
            console.log('Partial timezone match:', country.name);
            return createFallbackLocation(country, lat, lng);
          }
        }
      }
    }
    
    // Détecter par coordonnées approximatives
    return getLocationByCoordinates(lat, lng);
  }, []);

  const createFallbackLocation = (country, lat, lng) => {
    return {
      city: country.capital || '',
      countryCode: country.code,
      countryName: country.name,
      countryFlag: country.flag,
      coordinates: lat && lng ? { lat, lng } : null,
      isGPSDetected: true,
      isFallback: true,
      source: 'timezone_fallback'
    };
  };

  const getLocationByCoordinates = useCallback((lat, lng) => {
    console.log('Getting location by coordinates:', lat, lng);
    
    if (lat && lng) {
      // Détection par région géographique
      if (lat >= 35 && lat <= 70 && lng >= -10 && lng <= 40) {
        const france = countriesData.find(c => c.code === 'FR');
        console.log('Europe region -> France');
        return {
          city: france?.capital || 'Paris',
          countryCode: 'FR',
          countryName: france?.name || 'France',
          countryFlag: france?.flag || '🇫🇷',
          coordinates: { lat, lng },
          isGPSDetected: true,
          isFallback: true,
          source: 'europe_fallback'
        };
      }
      
      if (lat >= 25 && lat <= 70 && lng >= -170 && lng <= -50) {
        const usa = countriesData.find(c => c.code === 'US');
        console.log('North America region -> USA');
        return {
          city: 'Washington',
          countryCode: 'US',
          countryName: usa?.name || 'United States',
          countryFlag: usa?.flag || '🇺🇸',
          coordinates: { lat, lng },
          isGPSDetected: true,
          isFallback: true,
          source: 'north_america_fallback'
        };
      }
      
      if (lat >= -35 && lat <= 0 && lng >= -80 && lng <= -30) {
        const brazil = countriesData.find(c => c.code === 'BR');
        console.log('South America region -> Brazil');
        return {
          city: brazil?.capital || 'Brasília',
          countryCode: 'BR',
          countryName: brazil?.name || 'Brazil',
          countryFlag: brazil?.flag || '🇧🇷',
          coordinates: { lat, lng },
          isGPSDetected: true,
          isFallback: true,
          source: 'south_america_fallback'
        };
      }
      
      if (lat >= 0 && lat <= 40 && lng >= 70 && lng <= 140) {
        const china = countriesData.find(c => c.code === 'CN');
        console.log('East Asia region -> China');
        return {
          city: china?.capital || 'Beijing',
          countryCode: 'CN',
          countryName: china?.name || 'China',
          countryFlag: china?.flag || '🇨🇳',
          coordinates: { lat, lng },
          isGPSDetected: true,
          isFallback: true,
          source: 'east_asia_fallback'
        };
      }
    }
    
    // Fallback par défaut - France
    console.log('Default fallback -> France');
    const france = countriesData.find(c => c.code === 'FR');
    return {
      city: france?.capital || 'Paris',
      countryCode: 'FR',
      countryName: france?.name || 'France',
      countryFlag: france?.flag || '🇫🇷',
      coordinates: lat && lng ? { lat, lng } : null,
      isGPSDetected: true,
      isFallback: true,
      source: 'default_fallback'
    };
  }, []);

  // Filtrer les pays
  const filteredCountries = useMemo(() => {
    if (countrySearch.trim() === '') {
      return countriesData;
    }
    
    const searchLower = countrySearch.toLowerCase();
    return countriesData.filter(country =>
      country.name.toLowerCase().includes(searchLower) ||
      country.nativeName?.toLowerCase().includes(searchLower) ||
      country.capital?.toLowerCase().includes(searchLower) ||
      country.region?.toLowerCase().includes(searchLower) ||
      country.code.toLowerCase().includes(searchLower)
    );
  }, [countrySearch]);

  // Obtenir les villes pour un pays sélectionné
  const getCitiesForCountry = useMemo(() => {
    if (!location?.countryCode) return [];
    
    const country = countriesData.find(c => c.code === location.countryCode);
    if (!country || !country.cities) return [];
    
    if (citySearch.trim() === '') {
      return country.cities;
    }
    
    const searchLower = citySearch.toLowerCase();
    return country.cities.filter(city =>
      city.toLowerCase().includes(searchLower)
    );
  }, [location?.countryCode, citySearch]);

  const handleCountrySelect = useCallback((country) => {
    onLocationChange({
      ...location,
      countryCode: country.code,
      countryName: country.name,
      countryFlag: country.flag,
      city: '',
      isGPSDetected: false
    });
    setShowCountryModal(false);
    setCountrySearch('');
  }, [location, onLocationChange]);

  const handleCitySelect = useCallback((city) => {
    onLocationChange({
      ...location,
      city: city,
      isGPSDetected: false
    });
    setShowCityModal(false);
    setCitySearch('');
    
    if (city && location.countryCode) {
      const country = countriesData.find(c => c.code === location.countryCode);
      saveToRecent({
        city: city,
        countryCode: location.countryCode,
        countryName: country?.name,
        countryFlag: country?.flag
      });
    }
  }, [location, onLocationChange, saveToRecent]);

  const handleAddressChange = useCallback((e) => {
    onLocationChange({
      ...location,
      address: e.target.value
    });
  }, [location, onLocationChange]);

  const handleUseRecentLocation = useCallback((recentLoc) => {
    onLocationChange({
      ...location,
      city: recentLoc.city,
      countryCode: recentLoc.countryCode,
      countryName: recentLoc.countryName,
      countryFlag: recentLoc.countryFlag,
      isGPSDetected: false
    });
  }, [location, onLocationChange]);

  const clearLocation = useCallback(() => {
    if (window.confirm(t('locationInput.clear.message'))) {
      onLocationChange({
        city: '',
        countryCode: '',
        countryName: '',
        countryFlag: '',
        address: '',
        coordinates: null,
        isGPSDetected: false
      });
    }
  }, [onLocationChange, t]);

  // Fonction de test pour le développement
  const testWithMockLocation = useCallback((mockCoords) => {
    if (process.env.NODE_ENV === 'development') {
      setIsDetecting(true);
      setError('');
      
      const { latitude, longitude } = mockCoords;
      console.log('Testing with mock coordinates:', mockCoords);
      
      // Simuler la géolocalisation
      setTimeout(async () => {
        try {
          const geocodedData = await geocodeCoordinates(latitude, longitude);
          
          let country = null;
          if (geocodedData.countryCode) {
            country = countriesData.find(c => c.code === geocodedData.countryCode);
          }
          
          const locationData = {
            city: geocodedData.city || 'Mock City',
            countryCode: geocodedData.countryCode || 'FR',
            countryName: country?.name || 'France',
            countryFlag: country?.flag || '🇫🇷',
            coordinates: { lat: latitude, lng: longitude },
            address: geocodedData.locality || 'Mock Address',
            isGPSDetected: true,
            isTest: true
          };

          onLocationChange({
            ...location,
            ...locationData
          });

          localStorage.setItem('userLocation', JSON.stringify(locationData));
          await saveToRecent(locationData);
          
          setIsDetecting(false);
        } catch (error) {
          console.error('Mock location test failed:', error);
          setError('Test failed: ' + error.message);
          setIsDetecting(false);
        }
      }, 1000);
    }
  }, [geocodeCoordinates, location, onLocationChange, saveToRecent]);

  // Render Item pour la liste des pays
  const renderCountryItem = useCallback((country) => (
    <div
      className="country-item"
      onClick={() => handleCountrySelect(country)}
      key={country.code}
    >
      <span className="country-flag">{country.flag}</span>
      <div className="country-info">
        <span className="country-name">{country.name}</span>
        <span className="country-details">
          {country.capital && `${country.capital} • `}{country.region}
        </span>
      </div>
      <Icon name="chevron_right" size={20} color="#D1D5DB" />
    </div>
  ), [handleCountrySelect]);

  // Render Item pour la liste des villes
  const renderCityItem = useCallback((city, index) => (
    <div
      className="city-item"
      onClick={() => handleCitySelect(city)}
      key={`${city}-${index}`}
    >
      <div className="city-icon-container">
        <Icon name="location_city" size={20} color="#3B82F6" />
      </div>
      <span className="city-name">{city}</span>
      <Icon name="chevron_right" size={20} color="#D1D5DB" />
    </div>
  ), [handleCitySelect]);

  return (
    <div className="location-input-container">
      <div className="location-input-content">
        {/* En-tête avec actions */}
        <div className="location-header">
          <div className="header-content">
            <h1 className="header-title">{t('locationInput.header.title')}</h1>
            <p className="header-subtitle">
              {t('locationInput.header.subtitle')}
            </p>
          </div>
          
          <div className="header-actions">
            <button
              className="action-button"
              onClick={detectCurrentLocation}
              disabled={isDetecting}
              type="button"
            >
              {isDetecting ? (
                <div className="spinner"></div>
              ) : (
                <Icon name="my_location" size={16} color="#007AFF" />
              )}
              <span>{t('locationInput.buttons.locateMe')}</span>
            </button>
            
          
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="error-container">
            <Icon name="error" size={20} color="#DC2626" />
            <div className="error-content">
              <span className="error-text">{error}</span>
              <button 
                onClick={() => setError('')}
                className="error-close"
                aria-label="Close error"
                type="button"
              >
                <Icon name="close" size={16} color="#DC2626" />
              </button>
            </div>
          </div>
        )}

        {/* Debug info en développement */}
        {process.env.NODE_ENV === 'development' && geocodingAttempts.length > 0 && (
          <div className="debug-container">
            <details>
              <summary>API Attempts ({geocodingAttempts.length})</summary>
              <pre>{JSON.stringify(geocodingAttempts, null, 2)}</pre>
            </details>
          </div>
        )}

        {/* Localisations récentes */}
        {recentLocations.length > 0 && (
          <div className="recent-section">
            <h2 className="section-title">
              <Icon name="access_time" size={16} color="#6B7280" /> {t('locationInput.recent.title')}
            </h2>
            <div className="recent-scroll">
              {recentLocations.map((recentLoc, index) => (
                <button
                  key={index}
                  className={`recent-card ${
                    location?.countryCode === recentLoc.countryCode && 
                    location?.city === recentLoc.city ? 'active' : ''
                  }`}
                  onClick={() => handleUseRecentLocation(recentLoc)}
                  type="button"
                >
                  <span className="recent-flag">{recentLoc.countryFlag}</span>
                  <span className="recent-city" title={recentLoc.city}>
                    {recentLoc.city}
                  </span>
                  <span className="recent-country" title={recentLoc.countryName}>
                    {recentLoc.countryName}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sélection du pays avec modal */}
        <div className="section">
          <label className="input-label">
            {t('locationInput.fields.country')} <span className="required">*</span>
          </label>
          <button
            className={`selector-button ${!location?.countryCode ? 'empty' : ''}`}
            onClick={() => setShowCountryModal(true)}
            type="button"
          >
            <div className="selector-left">
              <Icon name="public" size={22} color={location?.countryCode ? "#3B82F6" : "#666"} />
              <div className="selector-content">
                {location?.countryCode ? (
                  <span className="selector-value">
                    {location.countryFlag} {location.countryName}
                  </span>
                ) : (
                  <span className="selector-placeholder">
                    {t('locationInput.selectors.countryPlaceholder')}
                  </span>
                )}
              </div>
            </div>
            <Icon name="arrow_drop_down" size={22} color="#9CA3AF" />
          </button>
        </div>

        {/* Sélection de la ville avec modal */}
        {location?.countryCode && (
          <div className="section">
            <label className="input-label">
              {t('locationInput.fields.city')} <span className="required">*</span>
            </label>
            <button
              className={`selector-button ${!location?.city ? 'empty' : ''}`}
              onClick={() => setShowCityModal(true)}
              type="button"
            >
              <div className="selector-left">
                <Icon name="location_city" size={22} color={location?.city ? "#3B82F6" : "#666"} />
                <div className="selector-content">
                  {location?.city ? (
                    <span className="selector-value">{location.city}</span>
                  ) : (
                    <span className="selector-placeholder">
                      {t('locationInput.selectors.cityPlaceholder')}
                    </span>
                  )}
                </div>
              </div>
              <Icon name="arrow_drop_down" size={22} color="#9CA3AF" />
            </button>
          </div>
        )}

        {/* Adresse complète (optionnelle) */}
        <div className="section">
          <Input
            label={t('locationInput.fields.fullAddress')}
            placeholder={t('locationInput.fields.fullAddressPlaceholder')}
            value={location?.address || ''}
            onChange={handleAddressChange}
            multiline
            rows={3}
            description={t('locationInput.fields.fullAddressDescription')}
            leftIcon={<Icon name="home" size={18} color="#666" />}
          />
        </div>

        {/* Validation des champs requis */}
        {location?.countryCode && location?.city && (
          <div className="validation-container">
            <Icon name="check_circle" size={24} color="#10B981" />
            <div className="validation-content">
              <span className="validation-title">{t('locationInput.validation.title')}</span>
              <span className="validation-text">
                {t('locationInput.validation.text')}
              </span>
            </div>
          </div>
        )}

        {/* Aperçu de la localisation */}
        {(location?.city || location?.countryName) && (
          <div className="preview-container">
            <div className="preview-header">
              <Icon name="place" size={22} color="#3B82F6" />
              <div className="preview-title-container">
                <h3 className="preview-title">{t('locationInput.preview.title')}</h3>
                <span className="preview-subtitle">
                  {location.isFallback && t('locationInput.preview.fallback')}
                  {location.isTest && t('locationInput.preview.test')}
                  {location.source && `Source: ${location.source}`}
                </span>
              </div>
            </div>
            
            <div className="preview-content">
              <div className="preview-badge">
                <span className="preview-badge-text">
                  {location.city}, {location.countryName}
                </span>
              </div>
              
              <div className="preview-info">
                <Icon name="info" size={16} color="#6B7280" />
                <span className="preview-info-text">
                  {t('locationInput.preview.info')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Message d'information */}
        <div className="info-container">
          <div className="info-header">
            <Icon name="security" size={20} color="#6B7280" />
            <h3 className="info-title">{t('locationInput.privacy.title')}</h3>
          </div>
          
          <div className="info-tips">
            <div className="info-tip">
              <div className="info-icon">
                <Icon name="visibility_off" size={14} color="#6B7280" />
              </div>
              <span className="info-text">
                {t('locationInput.privacy.tip1')}
              </span>
            </div>
            
            <div className="info-tip">
              <div className="info-icon">
                <Icon name="my_location" size={14} color="#6B7280" />
              </div>
              <span className="info-text">
                {t('locationInput.privacy.tip2')}
              </span>
            </div>
            
            <div className="info-tip">
              <div className="info-icon">
                <Icon name="handshake" size={14} color="#6B7280" />
              </div>
              <span className="info-text">
                {t('locationInput.privacy.tip3')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal pour la sélection du pays avec react-modal */}
      <Modal
        isOpen={showCountryModal}
        onRequestClose={() => {
          setShowCountryModal(false);
          setCountrySearch('');
        }}
        style={modalStyles}
        contentLabel="Select Country"
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className="modal-container">
          <div className="modal-header">
            <div className="modal-title-container">
              <button
                className="modal-back-button"
                onClick={() => {
                  setShowCountryModal(false);
                  setCountrySearch('');
                }}
                aria-label="Back"
                type="button"
              >
                <Icon name="arrow_back" size={24} color="#111827" />
              </button>
              <div>
                <h2 className="modal-title">{t('locationInput.modals.country.title')}</h2>
                <span className="modal-subtitle">
                  {t('locationInput.modals.country.subtitle', { count: filteredCountries.length })}
                </span>
              </div>
            </div>
          </div>

          <div className="search-container">
            <Icon name="search" size={22} color="#6B7280" className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder={t('locationInput.modals.searchCountry')}
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              autoFocus
            />
            {countrySearch.length > 0 && (
              <button
                className="clear-search-button"
                onClick={() => setCountrySearch('')}
                aria-label="Clear search"
                type="button"
              >
                <Icon name="close" size={20} color="#9CA3AF" />
              </button>
            )}
          </div>

          <div className="list-container">
            {filteredCountries.length > 0 ? (
              filteredCountries.map(country => renderCountryItem(country))
            ) : (
              <div className="empty-container">
                <Icon name="public_off" size={60} color="#D1D5DB" />
                <span className="empty-text">{t('locationInput.modals.noCountries')}</span>
                <span className="empty-subtext">
                  {t('locationInput.modals.tryOtherSearch')}
                </span>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal pour la sélection de la ville avec react-modal */}
      <Modal
        isOpen={showCityModal}
        onRequestClose={() => {
          setShowCityModal(false);
          setCitySearch('');
        }}
        style={modalStyles}
        contentLabel="Select City"
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className="modal-container">
          <div className="modal-header">
            <div className="modal-title-container">
              <button
                className="modal-back-button"
                onClick={() => {
                  setShowCityModal(false);
                  setCitySearch('');
                }}
                aria-label="Back"
                type="button"
              >
                <Icon name="arrow_back" size={24} color="#111827" />
              </button>
              <div>
                <h2 className="modal-title">{t('locationInput.modals.city.title')}</h2>
                <span className="modal-subtitle">
                  {t('locationInput.modals.city.subtitle', { 
                    country: location?.countryName,
                    count: getCitiesForCountry.length
                  })}
                </span>
              </div>
            </div>
          </div>

          <div className="search-container">
            <Icon name="search" size={22} color="#6B7280" className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder={t('locationInput.modals.searchCity')}
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              autoFocus
            />
            {citySearch.length > 0 && (
              <button
                className="clear-search-button"
                onClick={() => setCitySearch('')}
                aria-label="Clear search"
                type="button"
              >
                <Icon name="close" size={20} color="#9CA3AF" />
              </button>
            )}
          </div>

          <div className="list-container">
            {getCitiesForCountry.length > 0 ? (
              getCitiesForCountry.map((city, index) => renderCityItem(city, index))
            ) : (
              <div className="empty-container">
                <Icon name="location_city" size={60} color="#D1D5DB" />
                <span className="empty-text">{t('locationInput.modals.noCities')}</span>
                <span className="empty-subtext">
                  {citySearch 
                    ? t('locationInput.modals.tryOtherSearch')
                    : t('locationInput.modals.noCitiesForCountry')
                  }
                </span>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LocationInput;